// Sidebar entry + instructions for wiring Cursor / Claude / ChatGPT to the
// same API the dashboard uses. The key is shown once; spend still needs a
// human on the action queue.

import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { track } from "../lib/analytics";

type Agent = "cursor" | "claude" | "other";

const PLACEHOLDER = "fk_live_…";
const DOCS = "https://funnelkeeper.com/docs/mcp/";

const AGENTS: Array<{ id: Agent; label: string; hint: string }> = [
  {
    id: "cursor",
    label: "Cursor",
    hint: "Paste into ~/.cursor/mcp.json (Settings → MCP). Don't commit the key.",
  },
  {
    id: "claude",
    label: "Claude Code",
    hint: "Run this in the terminal, then start a new Claude Code session.",
  },
  {
    id: "other",
    label: "Claude · ChatGPT",
    hint: "Paste into the client's MCP config (Claude Desktop: claude_desktop_config.json).",
  },
];

function mcpServer(key: string) {
  return {
    command: "npx",
    args: ["-y", "--package", "funnelkeeper", "funnelkeeper-mcp"],
    env: { FUNNELKEEPER_API_KEY: key },
  };
}

function mcpJson(key: string): string {
  return JSON.stringify({ mcpServers: { funnelkeeper: mcpServer(key) } }, null, 2);
}

function claudeCommand(key: string): string {
  return `claude mcp add funnelkeeper -e FUNNELKEEPER_API_KEY=${key} -- npx -y --package funnelkeeper funnelkeeper-mcp`;
}

function cursorDeeplink(key: string): string {
  const config = encodeURIComponent(btoa(JSON.stringify(mcpServer(key))));
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=funnelkeeper&config=${config}`;
}

export function ConnectLlmItem() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="nav-item"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          track("connect_llm_open");
          setOpen(true);
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>Connect your LLM</span>
        <span className="nav-badge keeper">MCP</span>
      </button>
      {open && <ConnectLlmModal onClose={() => setOpen(false)} />}
    </>
  );
}

function ConnectLlmModal({ onClose }: { onClose: () => void }) {
  const [agent, setAgent] = useState<Agent>("cursor");
  const [key, setKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<"key" | "snippet" | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [onClose]);

  const mint = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.auth.createKey("llm");
      track("api_key_created", { source: "connect_llm" });
      setKey(created.key);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const shown = key ?? PLACEHOLDER;
  const current = AGENTS.find((a) => a.id === agent) ?? AGENTS[0];
  const snippet = agent === "claude" ? claudeCommand(shown) : mcpJson(shown);

  const copy = async (which: "key" | "snippet", text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setError("Couldn't copy — select the text and copy it yourself.");
    }
  };

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Connect your LLM" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div>
          <h2>Connect your LLM</h2>
          <p className="sub" style={{ marginTop: 6 }}>
            Your agent reads the funnel and drafts growth work. Spend still needs your tap.
          </p>
        </div>

        <div className="field">
          <label>1. Mint an API key</label>
          {!key ? (
            <>
              <button type="button" className="btn-solid" disabled={busy} onClick={() => void mint()}>
                {busy ? "Minting…" : "Mint a key"}
              </button>
              <span className="form-note">Shown once. Reuse a stored key, or mint a fresh one here.</span>
            </>
          ) : (
            <>
              <div className="secret">{key}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button type="button" className="btn-ghost" onClick={() => void copy("key", key)}>
                  {copied === "key" ? "Copied" : "Copy key"}
                </button>
                <span className="form-note">Store it now. Revoke anytime in Settings.</span>
              </div>
            </>
          )}
        </div>

        <div className="field">
          <label>2. Add FunnelKeeper to your agent</label>
          <div className="llm-agent-pills" role="tablist" aria-label="Agent">
            {AGENTS.map((a) => (
              <button
                key={a.id}
                type="button"
                role="tab"
                aria-selected={agent === a.id}
                className={agent === a.id ? "btn-accent" : "btn-ghost"}
                style={{ minHeight: 36, padding: "0 12px" }}
                onClick={() => setAgent(a.id)}
              >
                {a.label}
              </button>
            ))}
          </div>
          <span className="form-note">{current.hint}</span>
        </div>

        <div className="field">
          <div className="secret" style={{ whiteSpace: "pre-wrap" }}>{snippet}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {agent === "cursor" && key && (
              <a
                className="btn-accent"
                style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}
                href={cursorDeeplink(key)}
              >
                Add to Cursor
              </a>
            )}
            <button type="button" className="btn-ghost" onClick={() => void copy("snippet", snippet)}>
              {copied === "snippet" ? "Copied" : "Copy snippet"}
            </button>
            {!key && <span className="form-note">Mint a key — the snippet still has a placeholder.</span>}
          </div>
        </div>

        {error && <div className="form-error">{error}</div>}

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className="form-note" style={{ flex: 1 }}>
            Approvals stay human-gated.{" "}
            <a href={DOCS} target="_blank" rel="noreferrer">Tool list</a>
          </span>
          <button type="button" className="btn-text" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
